import { css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';

/**
 * Mobile button atom. Used in mobile navigation.
 * Background color alternates between connected and not connected mode.
 *
 * href - The mobile button href.
 * title - The mobile button text.
 * id - The mobile button id, must be unique.
 * displayArrow - Boolean. Default value: false. Add an "animated-button" class to the element.
 * customClass - custom class for rendering, cumulative. Default value : 'null'. (ie : primary-cta, secondary-cta, disabled)
 */
@customElement('nn-generic-button')
export class GenericButton extends LitElement {
  static styles = css`
    .generic-cta,
    .generic-cta:active {
      align-items: center;
      background: var(--nn-generic-button_primary-cta_background, #0e0e0e);
      border: 0;
      border-radius: var(--nn-generic-button_generic-cta_border-radius, 32px);
      box-shadow: none;
      cursor: pointer;
      display: inline-flex;
      font-family: 'font-medium';
      font-size: var(--nn-generic-button_generic-cta_font-size, 18px);
      height: 50px;
      justify-content: center;
      line-height: 1;
      margin: var(--nn-generic-button_generic-cta_margin, 0);
      max-width: 100%;
      padding: var(--nn-generic-button_generic-cta_padding, 0 24px);
      text-align: center;
      text-decoration: none;
      text-shadow: none;
      text-transform: none;
      transition: var(--nn-generic-button_generic-cta_transition, 0.3s);
      vertical-align: middle;
    }
    .generic-cta:hover {
      background: var(--nn-generic-button_generic-cta--hover_background, #000);
      color: var(--nn-generic-button_generic-cta--hover_color, #fff);
    }
    .disabled {
      cursor: not-allowed !important;
    }
    .primary-cta {
      background: var(--nn-generic-button_primary-cta_background, #0e0e0e);
      color: var(--nn-generic-button_primary-cta_color, #fff);
    }
    .primary-cta:hover {
      background: var(
        --nn-generic-button_primary-cta--hover_background,
        #494949
      );
    }
    .primary-cta.disabled,
    .primary-cta.disabled:hover {
      opacity: 0.7;
      background: var(
        --nn-generic-button_primary-cta-disabled_background,
        #cac8c8
      );
    }
    .secondary-cta {
      border: 1.5px solid
        var(--nn-generic-button_secondary-cta_border-color, #0e0e0e);
      background: transparent;
      color: var(--nn-generic-button_secondary-cta_color, #0e0e0e);
    }
    .secondary-cta:hover {
      background: var(
        --nn-generic-button_secondary-cta--hover_background,
        #494949
      );
      border: 1.5px solid
        var(--nn-generic-button_secondary-cta--hover_border-color, #494949);
    }
    .secondary-cta.disabled,
    .secondary-cta.disabled:hover {
      background: var(
        --nn-generic-button_secondary-cta-disabled_background,
        transparent
      );
      border: 1px solid
        var(--nn-generic-button_secondary-cta-disabled_border-color, #001018);
      color: var(--nn-generic-button_secondary-cta-disabled_color, #0e0e0e);
      opacity: 0.36;
    }
    .generic-cta.animated-button,
    .animated-button.generic-cta:hover {
      gap: var(--nn-generic-button_generic-cta-animated_gap, 0 12px);
      padding: var(--nn-generic-button_generic-cta-animated_padding, 0 32px);
    }
    .generic-cta.animated-button span,
    .animated-button.generic-cta:hover span {
      transition: var(
        --nn-generic-button_generic-cta-animated_span_transition,
        0.3s
      );
    }
    .generic-cta.animated-button:after,
    .animated-button.generic-cta:hover:after {
      border-bottom: 2px solid;
      border-left: 2px solid;
      content: '';
      display: inline-block;
      height: 8px;
      text-rendering: auto;
      transform: rotate(-135deg);
      transition: var(
        --nn-generic-button_generic-cta-animated-button--after_transition,
        0.3s
      );
      vertical-align: 1px;
      width: 8px;
      top: 2px;
    }
    .animated-button.generic-cta:hover {
      justify-content: space-between;
    }
    .animated-button.generic-cta:hover span {
      transform: translate(-16px);
    }
    .animated-button.generic-cta:hover:after {
      transform: rotate(-135deg) translate(-6px, 7px);
    }
  `;

  @property({ type: String }) href = '';

  @property({ type: String }) title = '';

  @property({ type: String }) id = '';

  @property({ type: String }) customClass = '';

  @property({ type: Boolean }) displayArrow = false;

  render() {
    return html`
      <a
        id="${this.id}"
        href="${this.href}"
        class="generic-cta${this.displayArrow &&
        !this.customClass.toLowerCase().includes('disabled')
          ? ' animated-button '
          : ' '}${this.customClass}"
        rel="nofollow"
        title="${this.title}"
      >
        <span>${this.title}</span>
      </a>
    `;
  }
}
